import React, { useState, useEffect, useRef } from "react";
import "./autocompletenotes.css";

/**
 * Notes input on Form Row that allows the user to have 
 * up to 5 suggestions when typing
 */

export default function AutocompleteNotes({ note, onNotesChange, height, handleResize }) {
  const [options, setOptions] = useState([]);
  const [inputText, setInputText] = useState(note || "");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);

  // Fetch suggestions based on the last word typed
  const fetchSuggestions = async (input) => {
    const words = (input || "").trim().split(" ");
    const lastWord = words[words.length - 1];
    if (lastWord) {
      try {
        const response = await fetch(
          `https://api.datamuse.com/sug?s=${lastWord}`
        );
        const data = await response.json();
        setOptions(
          data
            .filter((item) => item.word.toLowerCase() !== lastWord.toLowerCase())
            .slice(0, 5)
            .map((item) => item.word)
        );
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  };

  useEffect(() => {
      setInputText(note);
  }, [note]);

  useEffect(() => {
    fetchSuggestions(inputText);
  }, [inputText]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputText(newValue);
    setDropdownVisible(newValue.trim() !== "");
    onNotesChange(newValue);
    handleResize(e.target.scrollHeight); 

    if (newValue.endsWith(" ")) {
      setDropdownVisible(false);
    }
  };

  const handleOptionClick = (option) => {
    const words = inputText.split(" ");
    words[words.length - 1] = option;
    const newText = words.join(" ");
    setInputText(newText);
    onNotesChange(newText);
    setDropdownVisible(false);
  };

  //Close the dropdown with suggestions when the user clicks outside the container
  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setDropdownVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  //Style applied to the scrollbar when the browser is Firefox
  useEffect(() => {
    if (navigator.userAgent.includes("Firefox")) {
      document.body.classList.add("firefox");
    }
    return () => {
      document.body.classList.remove("firefox");
    };
  }, []);

  //Let the user pick suggestions through the keyboard
  const handleKeyDown = (e) => {
    if (dropdownVisible) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex === options.length - 1 ? 0 : prevIndex + 1
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex <= 0 ? options.length - 1 : prevIndex - 1
        );
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        handleOptionClick(options[highlightedIndex]);
      } else if (e.key === "Escape") {
        setDropdownVisible(false);
      }
    }
  };

  //Keep keyboard and mouse highlighted items synchronised
  const handleMouseEnter = (index) => {
    setHighlightedIndex(index);
  };

  const handleMouseLeave = () => {
    setHighlightedIndex(-1);
  };

  return (
    <div className="autocomplete-container" ref={containerRef}>
      <div className="autocomplete-input-wrapper" aria-multiline>
      <textarea
        style={{ height: height }}
        className="formrow-autocomplete-input"
        type="text"
        value={inputText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Write a note..."
      />
      </div>
      {dropdownVisible && options.length > 0 && (
        <div className="autocomplete-dropdown">
          <ul className="autocomplete-suggestions-list">
            {options.map((option, index) => (
              <li
                key={index}
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                className={`autocomplete-suggestion-item ${
                  index === highlightedIndex ? "highlighted" : ""
                }`}
              >
                {option}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
